/* eslint-disable consistent-return */

"use client";

import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useSession } from "next-auth/react";
import { useQueryClient } from "@tanstack/react-query";
import { io, Socket } from "socket.io-client";

import { USER_ABILITIES_QUERY_KEY } from "~/shared/hooks/useUserAbilities";
import { SocketRules } from "~/shared/websocket/model/business-rules/socketRules";

interface SocketContextType {
  socket: Socket | null;
}

const SocketContext = createContext<SocketContextType>({
  socket: null,
});

const createSocketInstance = (token: string | null) =>
  io(process.env.NEXT_PUBLIC_GENOME_EVENTS_URL, SocketRules.createSocketConfig(token ?? ""));

export const SocketProvider = ({ children }: { children: React.ReactNode }) => {
  const { data: session, update } = useSession();
  const queryClient = useQueryClient();

  const [socket, setSocket] = useState<Socket | null>(null);

  useEffect(() => {
    const activeToken = session?.user.px?.tk ?? session?.user.tk;
    if (!activeToken) return;

    const socketInstance = createSocketInstance(activeToken);

    const handleApiEvents = async (payload: {type: string}) => {
      if(payload.type === "permissions-updated"){
        await queryClient.invalidateQueries({ queryKey: USER_ABILITIES_QUERY_KEY });
        await update();
      }
    };

    socketInstance.on("events", handleApiEvents);

    setSocket(socketInstance);

    return () => {
      socketInstance.off("events", handleApiEvents);
      socketInstance.disconnect();
    };
  }, [queryClient, session?.user.px?.tk, session?.user.tk, update]);

  const contextValue = useMemo(
    () => ({ socket }),
    [socket]
  );

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => useContext(SocketContext);
